import { registry } from "zod";

// src/models/meta.ts
/** Field metadata uses Zod v4 `registry()` (not Zod v3). Peer dep: `"zod": "^4"`. */
/**
* PII / PHI category. Chosen to cover the 18 HIPAA Safe Harbor identifiers plus
* operationally sensitive data (credentials). Each category has a default
* {@link RedactStrategy} in {@link DEFAULT_STRATEGY_BY_CATEGORY}, overridable
* per field via {@link PiiMeta.redact}.
*/
let PiiCategory = /* @__PURE__ */ function(PiiCategory) {
	/** HIPAA Safe Harbor #1 */
	PiiCategory["Name"] = "name";
	/** HIPAA Safe Harbor #6 — kept distinct from phone for email-aware masking */
	PiiCategory["Email"] = "email";
	/** HIPAA Safe Harbor #4, #5 */
	PiiCategory["Phone"] = "phone";
	/** HIPAA Safe Harbor #2 — street-level postal address */
	PiiCategory["Address"] = "address";
	/** HIPAA Safe Harbor #2 — coarser geography (state, ZIP, coords) */
	PiiCategory["Geo"] = "geo";
	/** HIPAA Safe Harbor #15 */
	PiiCategory["IpAddress"] = "ipAddress";
	/** HIPAA Safe Harbor #14 */
	PiiCategory["Url"] = "url";
	/** HIPAA Safe Harbor #3 — dates tied to an individual (DOB, admit, discharge) */
	PiiCategory["Date"] = "date";
	/** Gender, race, ethnicity, age buckets (non-date demographic attributes) */
	PiiCategory["Demographic"] = "demographic";
	/** Clinical content: diagnoses, medications, notes, vitals */
	PiiCategory["Health"] = "health";
	/** PCI: card numbers, bank accounts, routing numbers */
	PiiCategory["Financial"] = "financial";
	/** HIPAA Safe Harbor #7, #11 — SSN, passport, driver's license */
	PiiCategory["GovernmentId"] = "governmentId";
	/** HIPAA Safe Harbor #8, #9 — MRN, insurance plan ID, beneficiary numbers */
	PiiCategory["MedicalId"] = "medicalId";
	/** HIPAA Safe Harbor #12, #13 — VIN, serial numbers, user-agent fingerprints */
	PiiCategory["DeviceId"] = "deviceId";
	/** HIPAA Safe Harbor #18 — generic unique codes (internal IDs, external vendor IDs) */
	PiiCategory["Identifier"] = "identifier";
	/** HIPAA Safe Harbor #16 — fingerprints, voiceprints, face embeddings */
	PiiCategory["Biometric"] = "biometric";
	/** HIPAA Safe Harbor #17 — full-face photos or comparable images */
	PiiCategory["Image"] = "image";
	/** Operationally sensitive (not PHI strictly): passwords, tokens, API keys, cookies */
	PiiCategory["Credential"] = "credential";
	/** Catch-all for free-form user input that may embed any of the above */
	PiiCategory["FreeText"] = "freeText";
	return PiiCategory;
}({});
/**
* How a tagged field is transformed when redacted.
* - {@link RedactStrategy.Redact}: replace with `[REDACTED]`
* - {@link RedactStrategy.Mask}: partial reveal (email-aware for {@link PiiCategory.Email})
* - {@link RedactStrategy.Hash}: stable deterministic hash — use for warehouse join keys
* - {@link RedactStrategy.TruncateYear}: keep year only (HIPAA Safe Harbor #3)
* - {@link RedactStrategy.TruncateZip3}: keep first 3 digits of US ZIP (HIPAA Safe Harbor #2)
* - {@link RedactStrategy.AnonymizeIp}: zero last octet (IPv4) / last 80 bits (IPv6)
* - {@link RedactStrategy.Drop}: remove the key entirely from the output
*/
let RedactStrategy = /* @__PURE__ */ function(RedactStrategy) {
	RedactStrategy["Redact"] = "redact";
	RedactStrategy["Mask"] = "mask";
	RedactStrategy["Hash"] = "hash";
	RedactStrategy["TruncateYear"] = "truncateYear";
	RedactStrategy["TruncateZip3"] = "truncateZip3";
	RedactStrategy["AnonymizeIp"] = "anonymizeIp";
	RedactStrategy["Drop"] = "drop";
	return RedactStrategy;
}({});
/**
* Default strategy for each category when {@link PiiMeta.redact} is not set.
* Chosen so logs are safe and the warehouse stays useful (IDs remain joinable
* via `hash`, dates keep year-level granularity, IPs keep subnet).
*/
const DEFAULT_STRATEGY_BY_CATEGORY = {
	["name"]: "mask",
	["email"]: "mask",
	["phone"]: "mask",
	["address"]: "redact",
	["geo"]: "truncateZip3",
	["ipAddress"]: "anonymizeIp",
	["url"]: "redact",
	["date"]: "truncateYear",
	["demographic"]: "redact",
	["health"]: "redact",
	["financial"]: "redact",
	["governmentId"]: "hash",
	["medicalId"]: "hash",
	["deviceId"]: "hash",
	["identifier"]: "hash",
	["biometric"]: "drop",
	["image"]: "drop",
	["credential"]: "redact",
	["freeText"]: "redact"
};
const metaRegistry = registry();
function shallowMergeFieldMeta(prev, next) {
	const merged = {
		...prev,
		...next
	};
	if (prev?.pii && next.pii) merged.pii = {
		...prev.pii,
		...next.pii
	};
	return merged;
}
/**
* Attach or merge {@link FieldMeta} on a Zod schema instance (same pattern as legacy `pii()`).
* Top-level keys shallow-merge; nested {@link FieldMeta.pii} objects merge so category and
* redact can be supplied in separate `meta(...)` calls.
*/
function meta(schema, partial) {
	const prev = metaRegistry.get(schema);
	metaRegistry.add(schema, shallowMergeFieldMeta(prev, partial));
	return schema;
}
/** Resolves the effective strategy: explicit override, else category default, else {@link RedactStrategy.Redact}. */
function resolveStrategy(piiMeta) {
	if (piiMeta.redact) return piiMeta.redact;
	if (piiMeta.category) return DEFAULT_STRATEGY_BY_CATEGORY[piiMeta.category];
	return "redact";
}
function readZodDef(schema) {
	return schema._zod?.def;
}
function unwrapWrappersForMeta(schema) {
	const def = readZodDef(schema);
	if (!def) return schema;
	const t = def.type;
	if (t === "optional" || t === "nullable" || t === "default" || t === "catch" || t === "prefault" || t === "readonly" || t === "nonoptional") {
		const inner = def.innerType;
		if (inner) return unwrapWrappersForMeta(inner);
	}
	if (t === "pipe") {
		const input = def.in;
		if (input) return unwrapWrappersForMeta(input);
	}
	return schema;
}
/**
* Field metadata for a Zod node, matching {@link piiUtils} lookup order
* (`schema` then unwrapped inner).
*/
function getFieldMeta(fieldSchema) {
	const inner = unwrapWrappersForMeta(fieldSchema);
	return metaRegistry.get(fieldSchema) ?? metaRegistry.get(inner);
}

export { DEFAULT_STRATEGY_BY_CATEGORY, PiiCategory, RedactStrategy, getFieldMeta, meta, metaRegistry, resolveStrategy };
//# sourceMappingURL=meta.mjs.map